import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Save, Eye, ImageIcon, CheckCircle2, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import Seo from "../../components/Seo";

const inp = "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm";
const STATUSES = ["Draft", "Needs Review", "Approved", "Scheduled", "Published", "Archived"];

function seoChecks(b) {
  const text = (b.content_html || "").replace(/<[^>]+>/g, " ");
  const words = text.trim().split(/\s+/).length;
  return [
    { label: "SEO Title length (30-60)", ok: b.seo_title && b.seo_title.length >= 30 && b.seo_title.length <= 60 },
    { label: "Meta description (70-160)", ok: b.meta_description && b.meta_description.length >= 70 && b.meta_description.length <= 160 },
    { label: "Primary keyword set", ok: !!b.primary_keyword },
    { label: "Has H2 structure", ok: /<h2/i.test(b.content_html || "") },
    { label: "Word count > 600", ok: words > 600 },
    { label: "Featured image", ok: !!b.featured_image },
    { label: "FAQs present", ok: (b.faqs || []).length >= 3 },
    { label: "Image alt text", ok: !!b.image_alt },
  ];
}

export default function BlogEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["blog-edit", id], queryFn: async () => (await api.get(`/admin/blog/${id}`)).data });
  useEffect(() => { if (data) setB(data); }, [data]);

  if (!b) return <div className="p-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  const set = (k, v) => setB((p) => ({ ...p, [k]: v }));

  const save = async (status) => {
    setSaving(true);
    try {
      const body = { title: b.title, content_html: b.content_html, excerpt: b.excerpt, category: b.category, seo_title: b.seo_title, meta_description: b.meta_description, image_alt: b.image_alt, faqs: b.faqs, primary_keyword: b.primary_keyword, featured: b.featured, slug: b.slug };
      if (status) body.status = status;
      const { data } = await api.put(`/admin/blog/${id}`, body); setB(data);
      toast.success(status === "Published" ? "Published!" : "Saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const regenImage = async () => {
    setImgBusy(true);
    try { const { data } = await api.post(`/admin/blog/${id}/image`, { prompt: b.title }); set("featured_image", data.featured_image); toast.success("Image regenerated"); }
    catch { toast.error("Image generation failed"); } finally { setImgBusy(false); }
  };

  const checks = seoChecks(b);
  const score = Math.round((checks.filter((c) => c.ok).length / checks.length) * 100);

  return (
    <div className="max-w-6xl">
      <Seo title={`Edit: ${b.title} | Admin`} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">Edit Article</h1>
        <div className="flex gap-2">
          {b.status === "Published" && <a href={`/blog/${b.slug}`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><ExternalLink className="w-4 h-4 mr-1" /> View Live</Button></a>}
          <Button variant="outline" size="sm" onClick={() => save()} disabled={saving} data-testid="blog-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Draft</Button>
          <Button size="sm" onClick={() => save("Published")} disabled={saving} className="bg-indigo-900 hover:bg-indigo-800" data-testid="blog-publish"><CheckCircle2 className="w-4 h-4 mr-1" /> Publish</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6 mt-6">
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <div><label className="text-sm font-medium text-slate-700">Title</label><input value={b.title} onChange={(e) => set("title", e.target.value)} className={`${inp} mt-1.5`} data-testid="blog-title" /></div>
            <div><label className="text-sm text-slate-600">Slug</label><input value={b.slug} onChange={(e) => set("slug", e.target.value)} className={`${inp} mt-1.5`} /></div>
            <div><label className="text-sm text-slate-600">Excerpt</label><textarea value={b.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} className={`${inp} mt-1.5`} /></div>
            <div><label className="text-sm text-slate-600">Content (HTML)</label><textarea value={b.content_html} onChange={(e) => set("content_html", e.target.value)} rows={20} className={`${inp} mt-1.5 font-mono text-xs`} data-testid="blog-content" /></div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-heading font-bold text-slate-900 mb-3">Live Preview</h3>
            <article className="blog-content border-t border-slate-100 pt-4" dangerouslySetInnerHTML={{ __html: b.content_html }} />
          </div>
        </div>

        <aside className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="font-heading font-bold text-slate-900 mb-3">Featured Image</h3>
            {b.featured_image ? <img src={fileUrl(b.featured_image)} alt={b.image_alt} className="w-full rounded-xl aspect-video object-cover" /> : <div className="w-full aspect-video rounded-xl bg-slate-100 flex items-center justify-center text-slate-400"><ImageIcon className="w-8 h-8" /></div>}
            <Button variant="outline" size="sm" onClick={regenImage} disabled={imgBusy} className="w-full mt-3" data-testid="blog-regen-image">{imgBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Regenerate Image</Button>
            <input value={b.image_alt || ""} onChange={(e) => set("image_alt", e.target.value)} placeholder="Image alt text" className={`${inp} mt-3`} />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3"><h3 className="font-heading font-bold text-slate-900">SEO Check</h3><span className={`font-heading font-extrabold ${score >= 80 ? "text-indigo-600" : "text-amber-500"}`}>{score}/100</span></div>
            <p className="text-xs text-slate-400 mb-3">Internal checklist, not a Google ranking score.</p>
            <ul className="space-y-1.5">{checks.map((c) => <li key={c.label} className={`text-xs flex items-center gap-2 ${c.ok ? "text-indigo-700" : "text-slate-400"}`}><CheckCircle2 className="w-3.5 h-3.5" /> {c.label}</li>)}</ul>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <h3 className="font-heading font-bold text-slate-900">Publishing</h3>
            <div><label className="text-xs text-slate-500">Status</label><Select value={b.status} onValueChange={(v) => set("status", v)}><SelectTrigger className="mt-1" data-testid="blog-status"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <div><label className="text-xs text-slate-500">Category</label><input value={b.category || ""} onChange={(e) => set("category", e.target.value)} className={`${inp} mt-1`} /></div>
            <div><label className="text-xs text-slate-500">SEO Title</label><input value={b.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className={`${inp} mt-1`} /></div>
            <div><label className="text-xs text-slate-500">Meta Description</label><textarea value={b.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} rows={3} className={`${inp} mt-1`} /></div>
            <div><label className="text-xs text-slate-500">Primary Keyword</label><input value={b.primary_keyword || ""} onChange={(e) => set("primary_keyword", e.target.value)} className={`${inp} mt-1`} /></div>
            {b.ai_generated && <div className="text-xs text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2">✨ AI generated — please review facts before publishing.</div>}
          </div>
        </aside>
      </div>
    </div>
  );
}
